// ---------------------------------------------------------------------------
// Enterprise module — Canary Releases management page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { DataTable, type Column } from '../components/DataTable';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Local types (mirrors @noxverify/release-controls types for UI consumption)
// ---------------------------------------------------------------------------

interface CanaryMetrics {
  errorRate: number;
  p99LatencyMs: number;
  requestCount: number;
}

interface CanaryRelease {
  id: string;
  name: string;
  version: string;
  rolloutPercentage: number;
  status: string;
  metrics: CanaryMetrics;
  startedAt: string;
  completedAt: string | null;
}

interface ReleaseGate {
  name: string;
  check: string;
  status: string;
  requiredForPromotion: boolean;
}

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

function buildReleaseColumns(
  t: ReturnType<typeof useTranslation>['t'],
  f: ReturnType<typeof useFormatters>,
): Column<CanaryRelease>[] {
  return [
    {
      key: 'name',
      header: t('releases.columns.release'),
      render: (r) => (
        <div>
          <span className="text-sm font-medium text-ink">{r.name}</span>
          <span className="ms-2 text-xs text-ink-muted">{r.version}</span>
        </div>
      ),
    },
    {
      key: 'rolloutPercentage',
      header: t('releases.columns.rollout'),
      render: (r) => (
        <div className="flex items-center gap-2">
          <div className="w-20 bg-surface-sunken rounded-sm h-2">
            <div
              className="bg-primary h-2 rounded-sm transition-all"
              style={{ width: `${r.rolloutPercentage}%` }}
            />
          </div>
          <span className="text-sm text-ink">{r.rolloutPercentage}%</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('releases.columns.status'),
      render: (r) => <StatusBadge value={r.status} />,
    },
    {
      key: 'errorRate',
      header: t('releases.columns.errorRate'),
      render: (r) => (
        <span
          className={`text-sm ${r.metrics.errorRate > 0.01 ? 'text-danger font-medium' : 'text-ink'}`}
        >
          {(r.metrics.errorRate * 100).toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'p99LatencyMs',
      header: t('releases.columns.p99Latency'),
      render: (r) => (
        <span
          className={`text-sm ${r.metrics.p99LatencyMs > 500 ? 'text-danger font-medium' : 'text-ink'}`}
        >
          {r.metrics.p99LatencyMs}ms
        </span>
      ),
    },
    {
      key: 'startedAt',
      header: t('releases.columns.started'),
      render: (r) => (
        <span className="text-sm text-ink-muted tabular-nums font-mono">
          {f.date(r.startedAt, { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      ),
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ReleasesPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const releaseColumns = useMemo(() => buildReleaseColumns(t, f), [t, f]);
  const [releases, setReleases] = useState<CanaryRelease[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<CanaryRelease | null>(
    null,
  );
  const [gates, setGates] = useState<ReleaseGate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);

    Promise.resolve()
      .then(() => {
        setReleases([]);
        setGates([]);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : t('releases.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  const activeReleases = releases.filter((r) => r.status === 'active' || r.status === 'paused');
  const historicalReleases = releases.filter(
    (r) => r.status === 'complete' || r.status === 'rolledback',
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('releases.headings.title')}</h1>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-hairline mb-6">
        <nav className="flex space-x-8" aria-label={t('releases.tabsAriaLabel')}>
          {(['active', 'history'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-ink-muted hover:text-ink hover:border-hairline-strong'
              }`}
            >
              {tab === 'active'
                ? t('releases.tabs.active', { count: activeReleases.length })
                : t('releases.tabs.history')}
            </button>
          ))}
        </nav>
      </div>

      {/* Active releases */}
      {activeTab === 'active' && (
        <div>
          {activeReleases.length === 0 ? (
            <p className="text-ink-muted text-center py-8">
              {t('releases.messages.noActive')}
            </p>
          ) : (
            <div className="space-y-4">
              {activeReleases.map((release) => (
                <div
                  key={release.id}
                  className="border border-hairline rounded-sm p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-ink">{release.name}</h3>
                      <span className="text-sm text-ink-muted">{release.version}</span>
                    </div>
                    <StatusBadge value={release.status} />
                  </div>

                  {/* Rollout bar */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 bg-surface-sunken rounded-sm h-3">
                      <div
                        className="bg-primary h-3 rounded-sm transition-all"
                        style={{ width: `${release.rolloutPercentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-ink w-12 text-end">
                      {release.rolloutPercentage}%
                    </span>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-ink-muted">{t('releases.metrics.errorRate')}</span>
                      <p
                        className={`font-medium ${release.metrics.errorRate > 0.01 ? 'text-danger' : 'text-primary'}`}
                      >
                        {(release.metrics.errorRate * 100).toFixed(2)}%
                      </p>
                    </div>
                    <div>
                      <span className="text-ink-muted">{t('releases.metrics.p99Latency')}</span>
                      <p
                        className={`font-medium ${release.metrics.p99LatencyMs > 500 ? 'text-danger' : 'text-primary'}`}
                      >
                        {release.metrics.p99LatencyMs}ms
                      </p>
                    </div>
                    <div>
                      <span className="text-ink-muted">{t('releases.metrics.requests')}</span>
                      <p className="font-medium text-ink tabular-nums font-mono">
                        {f.number(release.metrics.requestCount)}
                      </p>
                    </div>
                  </div>

                  {/* Release gates */}
                  {selectedRelease?.id === release.id && gates.length > 0 && (
                    <div className="border-t border-hairline pt-3 mb-3">
                      <h4 className="text-sm font-medium text-ink mb-2">
                        {t('releases.headings.gates')}
                      </h4>
                      <div className="space-y-1">
                        {gates.map((gate) => (
                          <div
                            key={gate.name}
                            className="flex items-center justify-between text-sm"
                          >
                            <span className="text-ink">{gate.name}</span>
                            <StatusBadge value={gate.status} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-medium bg-primary-tint hover:bg-primary-tint text-primary rounded transition-colors"
                      onClick={() => {
                        setSelectedRelease(release);
                      }}
                    >
                      {t('releases.actions.checkGates')}
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-medium bg-primary-tint hover:bg-primary-tint text-primary rounded transition-colors"
                      onClick={() => {
                        // In production: api.admin.promoteCanary(release.id, newPercentage)
                      }}
                    >
                      {t('releases.actions.promote')}
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm font-medium bg-danger-tint hover:bg-danger-tint text-danger rounded transition-colors"
                      onClick={() => {
                        // In production: api.admin.rollbackCanary(release.id, reason)
                      }}
                    >
                      {t('releases.actions.rollback')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History tab */}
      {activeTab === 'history' && (
        <DataTable
          columns={releaseColumns}
          data={historicalReleases}
          rowKey={(r) => r.id}
          emptyMessage={t('releases.history.empty')}
          ariaLabel={t('releases.history.ariaLabel')}
        />
      )}
    </div>
  );
}
